import { useState, useEffect } from 'react';
import { get } from '../utils/api';

export interface CompanyInfoAIContent {
  overview: string;
  priceMovement: string;
  catalysts: string[];
  risks: string[];
  baronAnalysis?: string[];
}

export interface CompanyInfoMetrics {
  marketCap: string | null;
  trailingPE: string | null;
  annualRevenue: string | null;
  quarterlyRevenueGrowth: string | null;
  priceToSales: string | null;
  debtToEquity: string | null;
  profitMargin: string | null;
  fiveYearDividendYield: string | null;
}

export interface CompanyInfoResponse {
  symbol: string;
  companyName: string;
  logoUrl: string | null;
  sector: string;
  aiContent: CompanyInfoAIContent;
  metrics: CompanyInfoMetrics;
}

interface UseCompanyInfoOptions {
  category: string;
  gameDate: string;
  enabled: boolean;
}

export function useCompanyInfo({ category, gameDate, enabled }: UseCompanyInfoOptions) {
  const [data, setData] = useState<CompanyInfoResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !category || !gameDate) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await get<CompanyInfoResponse>(
          `/api/${category}/company-info?date=${encodeURIComponent(gameDate)}`,
        );
        if (!cancelled && res.data) {
          setData(res.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load company info');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, category, gameDate]);

  return { data, loading, error };
}
